import type {
  CharacterDetailResponse,
  CharacterListResponse,
  CharacterResponse,
  CharacterSummary,
  FateMode,
  RollResponse,
  TonightResponse,
  UpdateCharacterRequest,
  Weights,
  WeightsResponse,
  WhereWasIResponse,
} from '@brimstone/shared';
import { className } from '@brimstone/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export const rosterKeys = {
  characters: ['characters'] as const,
  character: (id: string) => ['character', id] as const,
  tonight: ['tonight'] as const,
  where: (id: string) => ['where', id] as const,
  weights: ['weights'] as const,
};

export function useCharacters() {
  return useQuery({
    queryKey: rosterKeys.characters,
    queryFn: () => api<CharacterListResponse>('/api/characters'),
  });
}

export function useCharacter(id: string) {
  return useQuery({
    queryKey: rosterKeys.character(id),
    queryFn: () => api<CharacterDetailResponse>(`/api/characters/${id}`),
  });
}

export function useTonight() {
  return useQuery({
    queryKey: rosterKeys.tonight,
    queryFn: () => api<TonightResponse>('/api/tonight'),
  });
}

/** Changes a character's flags and refreshes everything that shows them. */
export function useUpdateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateCharacterRequest & { id: string }) =>
      api<CharacterResponse>(`/api/characters/${id}`, { method: 'PATCH', json: body }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: rosterKeys.characters });
      void qc.invalidateQueries({ queryKey: rosterKeys.tonight });
      void qc.invalidateQueries({ queryKey: rosterKeys.character(res.character.id) });
    },
  });
}

export function useRoll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mode: FateMode) =>
      api<RollResponse>('/api/tonight/roll', { method: 'POST', json: { mode } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: rosterKeys.tonight }),
  });
}

/** "Troll Mage", or whatever part is known. */
export function raceAndClass(c: Pick<CharacterSummary, 'race' | 'classToken'>): string {
  return [c.race, className(c.classToken)].filter(Boolean).join(' ') || '—';
}

export function useWhere(id: string | null) {
  return useQuery({
    queryKey: rosterKeys.where(id ?? ''),
    queryFn: () => api<WhereWasIResponse>(`/api/characters/${id}/where`),
    enabled: id !== null,
  });
}

export function useWeights() {
  return useQuery({
    queryKey: rosterKeys.weights,
    queryFn: () => api<WeightsResponse>('/api/settings/weights'),
  });
}

export function useSaveWeights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weights: Weights) =>
      api<WeightsResponse>('/api/settings/weights', { method: 'PUT', json: { weights } }),
    onSuccess: (res) => {
      qc.setQueryData(rosterKeys.weights, res);
      void qc.invalidateQueries({ queryKey: rosterKeys.tonight });
    },
  });
}
